import { queryClient, STALE_TIME_MS } from '@hub/app/queryClient';
import { HomePaths } from '@hub/domains/home/route.paths';
import { LoginPaths } from '@hub/domains/login/route.paths';
import type { FlagStore } from '@platform/feature-flags';
import type { QueryClient } from '@tanstack/react-query';
import type { ToOptions } from '@tanstack/react-router';
import { createRouter } from '@tanstack/react-router';
import { deLocalizeUrl, localizeUrl } from '../paraglide/runtime.js';
import { resolveBasepath } from './basepath';
import { DefaultErrorComponent } from './components/DefaultErrorComponent';
import { AUTHENTICATED_LAYOUT_ID } from './pages/_authenticated';
import { publicHref } from './publicHref';
import type { SettledRouterAuth } from './routerAuth';
import { routeTree } from './routeTree';

// Derived from the layout id so it can't drift from the route definition.
const AUTHENTICATED_LAYOUT_ROUTE_ID = `/${AUTHENTICATED_LAYOUT_ID}` as const;

export type RouterContext = {
  auth: SettledRouterAuth;
  queryClient: QueryClient;
  featureFlags: FlagStore | undefined;
};

export const router = createRouter({
  routeTree,
  // Single build serves both `/` (direct origin) and `/_hub` (via web-proxy).
  basepath: resolveBasepath(),
  rewrite: {
    input: ({ url }) => deLocalizeUrl(url),
    output: ({ url }) => localizeUrl(url),
  },
  context: {
    queryClient,
    // Placeholder until AuthedRouterProvider passes the live state. `unresolved` is the
    // fail-safe: were it ever read, guards show the error page instead of logging a user out.
    auth: { status: 'unresolved' },
    featureFlags: undefined,
  },
  // No defaultPendingComponent — routes that need a loading skeleton should handle it
  // at the component level. A router-level pending component causes a double flash:
  // the default skeleton renders first, then the route component mounts and shows
  // its own domain-specific skeleton, resulting in a visible layout shift.
  defaultPreload: false, // safe default, upgraded by RouterPreloadSync
  defaultPreloadDelay: 75,
  defaultPreloadStaleTime: STALE_TIME_MS,
  defaultPendingMs: 400,
  defaultPendingMinMs: 500,
  defaultErrorComponent: DefaultErrorComponent,
});

// Patch the router in place on route-tree HMR. Without this, editing a manifest
// re-runs this module and mints a new router while the mounted RouterProvider
// keeps the old one, desyncing the tree from the URL and flashing not-found.
if (import.meta.hot) {
  import.meta.hot.accept('./routeTree', (next) => {
    const mod = next as typeof import('./routeTree') | undefined;
    if (!mod) {
      return;
    }
    // Re-pass the live context so update() doesn't reset auth/flags to the initial value.
    router.update({ routeTree: mod.routeTree, context: router.options.context });
    void router.invalidate();
  });
}

// Reads the singleton directly: callers like AuthProvider mount above RouterProvider,
// so useMatches isn't available.
export function isAuthenticatedLocation(): boolean {
  return router.state.matches.some((match) => match.routeId === AUTHENTICATED_LAYOUT_ROUTE_ID);
}

// buildLocation applies the router's own rewrite, so the localized href (e.g. /kunden-login)
// stays correct without depending on paraglide URL patterns (absent in the standalone
// generate-paraglide build). `to` is the post-login return target (read back via `useReturnTo`) —
// without it, re-login after a logout from an authenticated page has nowhere to return to and
// falls back to the flag-gated home page, which 404s where the flag is off.
export function localizedLoginHref(to?: string): string {
  return router.buildLocation({ to: LoginPaths.login.path, search: { to } }).publicHref;
}

export function localizedLogoutConfirmationHref(to?: string): string {
  return router.buildLocation({ to: LoginPaths.logoutConfirmation.path, search: { to } }).publicHref;
}

// Through `publicHref`, not `buildLocation`: home takes no search or params, and that keeps one
// implementation of the rewrite for the guards to share (see `./publicHref`).
export function localizedHomeHref(): string {
  return publicHref(HomePaths.home.path);
}

// Generic form of the three above: the public href (basepath + locale) for callers that leave the app.
export function localizedHref(to: ToOptions): string {
  return router.buildLocation(to).publicHref;
}
